import { useState } from 'react';
import { Incident, IncidentWithKanbanSummary } from '../types/incident';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Clock, CheckCircle, XCircle, Hourglass, Search, Trash2 } from 'lucide-react';
import { cn } from '../utils/cn';
import { motion } from 'framer-motion';
import { CreatorProfile } from './CreatorProfile';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useIsMobile } from '../hooks/useIsMobile';
import { IncidentSummaryBadges } from './incidents/IncidentSummaryBadges';

interface IncidentCardProps {
  incident: Incident | IncidentWithKanbanSummary;
  canDelete: boolean;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
}

function hasKanbanSummary(i: Incident | IncidentWithKanbanSummary): i is IncidentWithKanbanSummary {
  return (
    'kanban_links_count' in i &&
    typeof (i as IncidentWithKanbanSummary).kanban_links_count === 'number'
  );
}

const StatusIcon = ({ status }: { status: Incident['status'] }) => {
  const iconProps = { className: "w-4 h-4" };

  switch (status) {
    case 'critical':
      return <span className="icon-tile icon-tile-danger"><AlertTriangle {...iconProps} /></span>;
    case 'active':
      return <span className="icon-tile icon-tile-info"><Clock {...iconProps} /></span>;
    case 'resolved':
      return <span className="icon-tile icon-tile-success"><CheckCircle {...iconProps} /></span>;
    case 'closed':
      return <span className="icon-tile icon-tile-neutral"><XCircle {...iconProps} /></span>;
    case 'pending':
      return <span className="icon-tile icon-tile-warning"><Hourglass {...iconProps} /></span>;
    case 'investigating':
      return <span className="icon-tile icon-tile-purple"><Search {...iconProps} /></span>;
    default:
      return <span className="icon-tile icon-tile-neutral"><AlertTriangle {...iconProps} /></span>;
  }
};

const StatusBadge = ({ status }: { status: Incident['status'] }) => {
  const baseClasses = "badge badge-dot";

  switch (status) {
    case 'critical':
      return <span className={cn(baseClasses, "bg-red-500/15 text-red-300 border-red-500/25")}>Critical</span>;
    case 'active':
      return <span className={cn(baseClasses, "bg-blue-500/15 text-blue-300 border-blue-500/25")}>Active</span>;
    case 'resolved':
      return <span className={cn(baseClasses, "bg-green-500/15 text-green-300 border-green-500/25")}>Resolved</span>;
    case 'closed':
      return <span className={cn(baseClasses, "bg-gray-500/15 text-gray-300 border-gray-500/25")}>Closed</span>;
    case 'pending':
      return <span className={cn(baseClasses, "bg-yellow-500/15 text-yellow-300 border-yellow-500/25")}>Pending</span>;
    case 'investigating':
      return <span className={cn(baseClasses, "bg-purple-500/15 text-purple-300 border-purple-500/25")}>Investigating</span>;
    default:
      return <span className={cn(baseClasses, "badge-neutral")}>{status}</span>;
  }
};

const PriorityBadge = ({ priority }: { priority: number }) => {
  const getColor = () => {
    if (priority <= 2) return "bg-red-500/15 text-red-300 border-red-500/25";
    if (priority <= 4) return "bg-orange-500/15 text-orange-300 border-orange-500/25";
    if (priority <= 6) return "bg-yellow-500/15 text-yellow-300 border-yellow-500/25";
    if (priority <= 8) return "bg-blue-500/15 text-blue-300 border-blue-500/25";
    return "bg-gray-500/15 text-gray-300 border-gray-500/25";
  };

  return (
    <span className={cn("badge", getColor())} title={`Priority ${priority}`}>
      P{priority}
    </span>
  );
};

export const IncidentCard = ({ incident, canDelete, onDelete, onClick }: IncidentCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this incident? This action cannot be undone.')) {
      onDelete?.(incident.id);
    }
  };

  const handleClick = () => {
    onClick?.(incident.id);
  };

  // Get first few lines of description for preview
  const getPreview = (text: string, maxLines: number = 3) => {
    const lines = text.split('\n').slice(0, maxLines);
    return lines.join('\n') + (text.split('\n').length > maxLines ? '...' : '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        "glass-card glass-card-interactive p-4 space-y-3",
        incident.status === 'closed' && "opacity-60"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div className="flex flex-wrap justify-between items-start gap-2">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <StatusIcon status={incident.status} />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">
              {incident.title}
            </h3>
            <p className="text-xs text-glass-400 mt-0.5 tabular-nums">
              Created {formatDistanceToNow(new Date(incident.created_at), { addSuffix: true })} by <CreatorProfile creatorId={incident.creator_id} inline />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <StatusBadge status={incident.status} />
          <PriorityBadge priority={incident.priority} />
        </div>
      </div>

      {/* Markdown Preview */}
      {incident.description && (
        <div className="text-sm text-glass-300 line-clamp-3 overflow-hidden">
          <MarkdownRenderer content={getPreview(incident.description)} />
        </div>
      )}

      {/* Augmented summary badges (kanban links / open / done / assignees / watchers) */}
      {hasKanbanSummary(incident) && (
        <IncidentSummaryBadges
          summary={{
            kanban_links_count: incident.kanban_links_count,
            kanban_open_cards_count: incident.kanban_open_cards_count,
            kanban_done_cards_count: incident.kanban_done_cards_count,
            assignees_count: incident.assignees_count,
            watchers_count: incident.watchers_count,
          }}
          size="sm"
        />
      )}

      <div className="flex items-center justify-between gap-2 text-xs text-glass-500 min-h-[1.75rem]">
        <span className="truncate font-mono">{incident.id}</span>
        {(isHovered || isMobile) && (
          <div className="flex items-center gap-1">
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                title="Delete incident"
                aria-label="Delete incident"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

